import { Heart, ExternalLink, Check, Leaf } from "lucide-react";
import { ProductBadges } from "@/components/LovedBadge";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";

const HIDDEN = { opacity: 0, y: 14 };
const SHOWN = { opacity: 1, y: 0 };
const ONCE = { once: true };

export default function ProductCard({ product: p, onAdd, inList, delay = 0, highlight = false, testid = "catalog-product-card" }) {
  const cardTestId = testid;
  return (
    <motion.div
      initial={HIDDEN} whileInView={SHOWN}
      viewport={ONCE} transition={{ delay }}
      data-testid={cardTestId}
      className={`group rounded-2xl bg-white overflow-hidden transition-transform duration-300 hover:-translate-y-1 hover:shadow-lg flex flex-col border ${
        highlight ? "border-[#E5A8A0]" : "border-[#EAE3DC]"
      }`}
    >
      <Link to={`/prodotto/${p.id}`} data-testid={`product-link-${p.id}`} className="block">
        <div className="relative aspect-square overflow-hidden bg-[#FAF8F5]">
          {p.image ? (
            <img src={p.image} alt={p.title}
              className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
          ) : <div className="w-full h-full flex items-center justify-center text-[#C8BDB3]">Nessuna immagine</div>}
          <span className="absolute top-3 left-3 rounded-full bg-white/90 backdrop-blur text-[#4E7363] text-[11px] px-2.5 py-1">
            {p.category}
          </span>
          {p.gender === "neutro" && (
            <span data-testid={`neutral-badge-${p.id}`}
              className="absolute top-3 right-3 inline-flex items-center gap-1 rounded-full bg-[#EAF0EC] text-[#4E7363] text-[11px] px-2.5 py-1 border border-[#A7C4B5]">
              <Leaf size={11} /> Neutro
            </span>
          )}
          <ProductBadges product={p} className="absolute bottom-3 right-3" />
          {!p.available && (
            <span className="absolute bottom-3 left-3 rounded-full bg-[#B85750] text-white text-[11px] px-2.5 py-1">
              Esaurito
            </span>
          )}
        </div>
      </Link>
      <div className="p-3 sm:p-4 flex flex-col flex-1">
        <Link to={`/prodotto/${p.id}`} className="block">
          <h3 className="font-display text-[13px] sm:text-[15px] font-medium text-[#2D2825] leading-snug line-clamp-2 min-h-[36px] sm:min-h-[42px] hover:text-[#C87D75] transition-colors">
            {p.title}
          </h3>
        </Link>
        <div className="mt-1.5 flex flex-wrap items-center gap-1.5 text-[10px] sm:text-[11px] text-[#8C7F78]" data-testid={`material-tag-${p.id}`}>
          <span className="rounded-full bg-[#F4EFEA] px-2 py-0.5 text-[#5C534E]">{p.material}</span>
          <span className="hidden sm:inline">·</span>
          <span>{p.season_label}</span>
        </div>
        {p.sizes && p.sizes.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-1" data-testid={`sizes-${p.id}`}>
            {p.sizes.slice(0, 4).map((s) => (
              <span key={s} data-testid={s === p.suggested_size ? `suggested-size-${p.id}` : undefined}
                className={`rounded-md border text-[10px] sm:text-[11px] px-1.5 py-0.5 ${s === p.suggested_size ? "border-[#C87D75] bg-[#F9ECE9] text-[#A35A52] font-medium" : "border-[#EAE3DC] bg-[#FAF8F5] text-[#5C534E]"}`}>
                {s === p.suggested_size ? `✓ ${s}` : s}
              </span>
            ))}
            {p.sizes.length > 4 && (
              <span className="text-[10px] sm:text-[11px] text-[#8C7F78] px-1">+{p.sizes.length - 4}</span>
            )}
          </div>
        )}
        <div className="mt-2 font-serif text-xl sm:text-2xl text-[#C87D75]">
          {p.price ? `€ ${p.price}` : "—"}
        </div>
        <div className="mt-auto pt-3 sm:pt-4 flex flex-col sm:flex-row gap-2">
          <button
            data-testid={`add-to-list-${p.id}`} onClick={() => onAdd(p)}
            className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-full border border-[#E5A8A0] text-[#C87D75] hover:bg-[#F9ECE9] text-sm min-h-[44px] sm:min-h-0 py-2 transition-colors"
          >
            {inList ? <><Check size={14} /> In lista</> : <><Heart size={14} /> Lista</>}
          </button>
          <a
            data-testid={`buy-${p.id}`} href={p.url} target="_blank" rel="noreferrer"
            className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-full bg-[#2D2825] text-white hover:bg-[#473f3a] text-sm min-h-[44px] sm:min-h-0 py-2 transition-colors"
          >
            Acquista <ExternalLink size={13} />
          </a>
        </div>
      </div>
    </motion.div>
  );
}
